import { supabase } from '../lib/supabase';

/**
 * Knowledge Base Service
 * Manages category-specific knowledge bases with search and retrieval
 */

/**
 * Get all active knowledge base categories
 */
export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      ?.from('knowledge_base_categories')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('display_order', { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching categories:', error);
    return { data: null, error };
  }
};

/**
 * Get documents for a specific category
 */
export const getCategoryDocuments = async (categoryId, options = {}) => {
  try {
    let query = supabase
      ?.from('knowledge_base_documents')
      ?.select(`
        *,
        category:knowledge_base_categories(name, slug)
      `)
      ?.eq('category_id', categoryId)
      ?.eq('is_published', true)
      ?.order('created_at', { ascending: false });

    if (options?.limit) {
      query = query?.limit(options?.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching category documents:', error);
    return { data: null, error };
  }
};

/**
 * Search knowledge base with optional category filter
 */
export const searchKnowledgeBase = async (searchQuery, categoryId = null, limit = 10) => {
  try {
    const { data, error } = await supabase?.rpc('search_knowledge_base', {
      search_query: searchQuery,
      category_filter: categoryId,
      result_limit: limit,
    }) || { data: [], error: null };

    if (error) {
      console.warn('KB Search RPC error:', error);
      return { data: [], error: null }; // Return empty data instead of crashing
    }
    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error searching knowledge base:', error);
    return { data: [], error: null };
  }
};

/**
 * Get a single document by ID
 */
export const getDocument = async (documentId) => {
  try {
    const { data, error } = await supabase
      ?.from('knowledge_base_documents')
      ?.select(`
        *,
        category:knowledge_base_categories(name, slug),
        author:user_profiles(full_name)
      `)
      ?.eq('id', documentId)
      ?.eq('is_published', true)
      ?.single();

    if (error) throw error;

    // Increment view count
    await supabase
      ?.from('knowledge_base_documents')
      ?.update({ view_count: (data?.view_count || 0) + 1 })
      ?.eq('id', documentId);

    return { data, error: null };
  } catch (error) {
    console.error('Error fetching document:', error);
    return { data: null, error };
  }
};

/**
 * Get documents by category slug
 */
export const getDocumentsByCategorySlug = async (categorySlug, limit = 20) => {
  try {
    const { data: category, error: catError } = await supabase
      ?.from('knowledge_base_categories')
      ?.select('id')
      ?.eq('slug', categorySlug)
      ?.single();

    if (catError) throw catError;

    return await getCategoryDocuments(category?.id, { limit });
  } catch (error) {
    console.error('Error fetching documents by slug:', error);
    return { data: null, error };
  }
};

/**
 * Get relevant documents for AI context (admin function)
 */
export const getRelevantDocumentsForAI = async (query, categoryId = null, limit = 5) => {
  try {
    const { data, error } = await searchKnowledgeBase(query, categoryId, limit);
    if (error) throw error;

    // Get full content for matched documents
    if (data && data?.length > 0) {
      const documentIds = data?.map(d => d?.id);
      const { data: fullDocs, error: docError } = await supabase
        ?.from('knowledge_base_documents')
        ?.select('id, title, content, tags')
        ?.in('id', documentIds);

      if (docError) throw docError;
      return { data: fullDocs, error: null };
    }

    return { data: [], error: null };
  } catch (error) {
    console.error('Error getting relevant documents:', error);
    return { data: null, error };
  }
};

// Admin functions
export const adminKB = {
  /**
   * Create a new knowledge base document
   */
  async createDocument(documentData) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base_documents')
        ?.insert([
          {
            ...documentData,
            author_id: (await supabase?.auth?.getUser())?.data?.user?.id,
          },
        ])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating document:', error);
      return { data: null, error };
    }
  },

  /**
   * Update a knowledge base document
   */
  async updateDocument(documentId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base_documents')
        ?.update(updates)
        ?.eq('id', documentId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating document:', error);
      return { data: null, error };
    }
  },

  /**
   * Delete a knowledge base document
   */
  async deleteDocument(documentId) {
    try {
      const { error } = await supabase
        ?.from('knowledge_base_documents')
        ?.delete()
        ?.eq('id', documentId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting document:', error);
      return { error };
    }
  },

  /**
   * Get all documents (including unpublished)
   */
  async getAllDocuments(categoryId = null) {
    try {
      let query = supabase
        ?.from('knowledge_base_documents')
        ?.select(`
          *,
          category:knowledge_base_categories(name, slug),
          author:user_profiles(full_name)
        `)
        ?.order('created_at', { ascending: false });

      if (categoryId) {
        query = query?.eq('category_id', categoryId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching all documents:', error);
      return { data: null, error };
    }
  },

  /**
   * Create a new category
   */
  async createCategory(categoryData) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base_categories')
        ?.insert([categoryData])
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating category:', error);
      return { data: null, error };
    }
  },

  /**
   * Update a category
   */
  async updateCategory(categoryId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('knowledge_base_categories')
        ?.update(updates)
        ?.eq('id', categoryId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating category:', error);
      return { data: null, error };
    }
  },
};

export default {
  getCategories,
  getCategoryDocuments,
  searchKnowledgeBase,
  getDocument,
  getDocumentsByCategorySlug,
  getRelevantDocumentsForAI,
  adminKB,
};